import React, { useEffect, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { X, Loader2, Sparkles, Rocket, CheckCircle2, PenTool, TrendingUp, ArrowRight, Play } from "lucide-react";
import { ScoreRing } from "./ScoreRing";
import { Button } from "./ui/button";

const SLIDE_MS = 2500;

const slides = [
  {
    key: "analyse", label: "Analyse",
    render: () => (
      <div className="flex flex-col items-center gap-4">
        <Loader2 size={40} className="text-neon animate-spin" />
        <p className="font-display font-bold text-2xl">Analyse de votre entreprise en cours…</p>
        <p className="text-sm text-[hsl(var(--muted-foreground))]">LOCALBOOST AI étudie votre visibilité, votre réputation et votre potentiel.</p>
      </div>
    ),
  },
  {
    key: "score", label: "Business Score",
    render: () => (
      <div className="flex flex-col items-center gap-4">
        <ScoreRing value={74} size={180} />
        <p className="font-display font-bold text-xl">Votre Business Score : <span className="neon-text">74/100</span></p>
      </div>
    ),
  },
  {
    key: "opp", label: "Opportunités",
    render: () => (
      <div className="flex flex-col items-center gap-4">
        <div className="w-16 h-16 rounded-2xl bg-neon/15 flex items-center justify-center text-neon"><Sparkles size={30} /></div>
        <p className="font-display font-black text-3xl neon-text">6 opportunités détectées</p>
        <p className="text-sm text-[hsl(var(--muted-foreground))]">Prêtes à devenir des actions concrètes.</p>
      </div>
    ),
  },
  {
    key: "reco", label: "Recommandation",
    render: () => (
      <div className="w-full max-w-md">
        <div className="panel p-5">
          <div className="flex items-center gap-2 mb-2"><div className="w-9 h-9 rounded-xl bg-primary/20 flex items-center justify-center text-neon"><Rocket size={18} /></div><span className="font-semibold">Recommandation IA</span></div>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Lancer l'offre « Mardi Découverte » -20% et programmer 3 publications pour remplir vos heures creuses.</p>
        </div>
      </div>
    ),
  },
  {
    key: "mission", label: "Missions",
    render: () => (
      <div className="w-full max-w-md space-y-2">
        {[["Créer l'offre du mardi", "done"], ["Préparer le visuel Instagram", "done"], ["Programmer la campagne WhatsApp", "progress"]].map(([txt, st], i) => (
          <motion.div key={i} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.25 }}
            className="panel px-4 py-3 flex items-center gap-3">
            {st === "done" ? <CheckCircle2 size={18} className="text-emerald-400" /> : <Loader2 size={18} className="text-neon animate-spin" />}
            <span className={`text-sm ${st === "done" ? "line-through text-[hsl(var(--muted-foreground))]" : ""}`}>{txt}</span>
            <span className="ml-auto text-[10px] uppercase font-semibold" style={{ color: st === "done" ? "#10B981" : "#00E5FF" }}>{st === "done" ? "Terminée" : "En cours"}</span>
          </motion.div>
        ))}
      </div>
    ),
  },
  {
    key: "content", label: "Content Studio",
    render: () => (
      <div className="w-full max-w-md">
        <div className="panel p-5">
          <div className="flex items-center gap-2 mb-3"><PenTool size={16} className="text-neon" /><span className="font-semibold text-sm">Content Studio</span></div>
          <div className="space-y-2"><div className="gen-line w-[92%]" /><div className="gen-line w-full" /><div className="gen-line w-[70%]" /></div>
          <div className="flex gap-1.5 mt-3">{["#café", "#Lyon", "#MardiDécouverte"].map((h) => <span key={h} className="text-[10px] text-neon">{h}</span>)}</div>
        </div>
      </div>
    ),
  },
  {
    key: "progress", label: "Progression",
    render: () => (
      <div className="flex flex-col items-center gap-4">
        <div className="flex items-center gap-3 text-emerald-400"><TrendingUp size={30} /><span className="font-display font-black text-4xl">Business Score +3</span></div>
        <p className="text-sm text-[hsl(var(--muted-foreground))]">Analysez → Décidez → Agissez → Progressez. En continu.</p>
      </div>
    ),
  },
];

export function ShowcaseMode({ open, onClose, onDemo, onAnalyze }) {
  const reduce = useReducedMotion();
  const [i, setI] = useState(0);

  useEffect(() => {
    if (!open) return;
    setI(0);
    const id = setInterval(() => setI((p) => Math.min(p + 1, slides.length - 1)), SLIDE_MS);
    return () => clearInterval(id);
  }, [open]);

  const last = i === slides.length - 1;

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-center justify-center p-5" data-testid="showcase-overlay">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-xl" onClick={onClose} />
          <div className="absolute inset-0 aurora opacity-60 pointer-events-none" />

          <div className="relative z-10 w-full max-w-2xl">
            {/* progress dots */}
            <div className="flex gap-1.5 mb-6">
              {slides.map((s, idx) => (
                <div key={s.key} className="flex-1 h-1 rounded-full bg-white/10 overflow-hidden">
                  <motion.div className="h-full bg-gradient-to-r from-primary to-neon"
                    initial={false}
                    animate={{ width: idx < i ? "100%" : idx === i ? "100%" : "0%" }}
                    transition={{ duration: idx === i && !reduce ? SLIDE_MS / 1000 : 0.3, ease: "linear" }} />
                </div>
              ))}
            </div>

            <button data-testid="showcase-close" onClick={onClose} className="absolute -top-1 right-0 text-white/60 hover:text-white"><X size={22} /></button>

            <div className="lb-card p-8 lg:p-12 min-h-[300px] flex flex-col items-center justify-center text-center">
              <AnimatePresence mode="wait">
                <motion.div key={slides[i].key}
                  initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.4 }}
                  className="w-full flex flex-col items-center">
                  {slides[i].render()}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* footer */}
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
              <span className="text-xs text-[hsl(var(--muted-foreground))]">{slides[i].label} · {i + 1}/{slides.length}</span>
              {last ? (
                <div className="flex gap-2">
                  <Button data-testid="showcase-demo" onClick={onDemo} className="rounded-full h-11 px-6 bg-gradient-to-r from-primary to-neon text-white lb-glow-btn">Essayer la démo</Button>
                  <Button data-testid="showcase-analyze" onClick={onAnalyze} variant="outline" className="rounded-full h-11 px-6 border-white/15 bg-white/5 hover:bg-white/10 text-white">Analyser mon business <ArrowRight size={16} className="ml-1" /></Button>
                </div>
              ) : (
                <button data-testid="showcase-skip" onClick={() => setI(slides.length - 1)} className="text-xs text-neon flex items-center gap-1">Passer <ArrowRight size={13} /></button>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
